import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getApiUrl } from "../config/api";
import { ArrowLeft, Download, BarChart3, Filter } from "lucide-react";

// Utility function to format date without timezone conversion
const formatDate = (dateString) => {
  if (!dateString) return 'No date';
  // Split the date string and format it directly to avoid timezone issues
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${month}/${day}/${year}`;
};

export default function SuppliesReports() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({ from: "", to: "", vendor: "", product: "", brand: "", category: "" });
  const [viewMode, setViewMode] = useState("table"); // "table" or "graph"
  const [graphData, setGraphData] = useState([]);
  const [uniqueVendors, setUniqueVendors] = useState([]);
  const [uniqueProducts, setUniqueProducts] = useState([]);
  const [uniqueBrands, setUniqueBrands] = useState([]);
  const [uniqueCategories, setUniqueCategories] = useState([]);

  useEffect(() => { fetchOrders(); }, []);
  
  // Regenerate graph data when filters change
  useEffect(() => {
    if (orders.length > 0) {
      const filtered = applyFilters();
      generateGraphData(filtered);
    }
  }, [filters, orders]);

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.vendor) params.vendor = filters.vendor;
      const res = await axios.get(getApiUrl("/api/supplies/orders"), { params });
      const ordersData = res.data || [];
      setOrders(ordersData);
      
      // Extract unique values for filters
      const vendors = [...new Set(ordersData.map(o => o.vendor_name).filter(Boolean))];
      const products = [...new Set(ordersData.flatMap(o => o.lines?.map(l => l.product_name).filter(Boolean) || []))];
      const brands = [...new Set(ordersData.flatMap(o => o.lines?.map(l => l.brand_name).filter(Boolean) || []))];
      const categories = [...new Set(ordersData.flatMap(o => o.lines?.map(l => l.category_name).filter(Boolean) || []))];
      
      setUniqueVendors(vendors);
      setUniqueProducts(products);
      setUniqueBrands(brands);
      setUniqueCategories(categories);
      
      // Generate graph data
      generateGraphData(ordersData);
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  };

  const generateGraphData = (ordersData) => {
    // Group orders by month
    const monthlyData = {};
    
    ordersData.forEach(order => {
      const orderDate = new Date(order.order_date);
      const monthKey = `${orderDate.getFullYear()}-${String(orderDate.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthKey,
          total: 0,
          orders: 0
        };
      }
      
      monthlyData[monthKey].total += Number(order.grand_total) || 0;
      monthlyData[monthKey].orders += 1;
    });
    
    // If no date filters are applied, show full year based on data
    if (!filters.from && !filters.to) {
      // Find the year range from the actual data
      const years = [...new Set(ordersData.map(order => new Date(order.order_date).getFullYear()))];
      const targetYear = years.length > 0 ? Math.max(...years) : new Date().getFullYear();
      
      const fullYearData = [];
      
      // Generate all 12 months of the target year
      for (let month = 1; month <= 12; month++) {
        const monthKey = `${targetYear}-${String(month).padStart(2, '0')}`;
        fullYearData.push({
          month: monthKey,
          total: monthlyData[monthKey]?.total || 0,
          orders: monthlyData[monthKey]?.orders || 0
        });
      }
      
      
      setGraphData(fullYearData);
    } else {
      // Convert to array and sort by month (existing behavior for filtered data)
      const graphDataArray = Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
      setGraphData(graphDataArray);
    }
  };

  const applyFilters = () => {
    let filteredOrders = [...orders];
    
    if (filters.vendor) {
      filteredOrders = filteredOrders.filter(o => 
        o.vendor_name?.toLowerCase().includes(filters.vendor.toLowerCase())
      );
    }
    
    if (filters.product) {
      filteredOrders = filteredOrders.filter(o => 
        o.lines?.some(line => 
          line.product_name?.toLowerCase().includes(filters.product.toLowerCase())
        )
      );
    }
    
    if (filters.brand) {
      filteredOrders = filteredOrders.filter(o => 
        o.lines?.some(line => 
          line.brand_name?.toLowerCase().includes(filters.brand.toLowerCase())
        )
      );
    }
    
    if (filters.category) {
      filteredOrders = filteredOrders.filter(o => 
        o.lines?.some(line => 
          line.category_name?.toLowerCase().includes(filters.category.toLowerCase())
        )
      );
    }
    
    return filteredOrders;
  };

  const filteredOrders = applyFilters();
  const total = filteredOrders.reduce((sum, o) => sum + (Number(o.grand_total) || 0), 0);
  const subtotal = filteredOrders.reduce((sum, o) => sum + (Number(o.subtotal) || 0), 0);
  const tax = filteredOrders.reduce((sum, o) => sum + (Number(o.tax_total) || 0), 0);
  const shipping = filteredOrders.reduce((sum, o) => sum + (Number(o.shipping) || 0), 0);
  const other = filteredOrders.reduce((sum, o) => sum + (Number(o.other_total) || 0), 0);

  const exportToCSV = () => {
    const headers = ['Date', 'Vendor', 'Subtotal', 'Tax', 'Shipping', 'Other', 'Total', 'Currency', 'Notes'];
    const csvContent = [
      headers.join(','),
      ...filteredOrders.map(o => [
        formatDate(o.order_date),
        `"${(o.vendor_name || '').replace(/"/g, '""')}"`,
        Number(o.subtotal || 0).toFixed(2),
        Number(o.tax_total || 0).toFixed(2),
        Number(o.shipping || 0).toFixed(2),
        Number(o.other_total || 0).toFixed(2),
        Number(o.grand_total || 0).toFixed(2),
        o.currency || 'USD',
        `"${(o.notes || '').replace(/"/g, '""')}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `supplies-report-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-3 sm:p-6 overflow-x-hidden">
      {/* Mobile Header */}
      <div className="sm:hidden mb-4">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => navigate('/supplies')} className="inline-flex items-center px-2 py-1 border rounded hover:bg-gray-50 text-xs">
            <ArrowLeft className="w-3 h-3 mr-1" /> Back
          </button>
          <h2 className="text-lg font-semibold">Supplies Reports</h2>
          <div></div>
        </div>
        <div className="flex gap-1">
          <button 
            onClick={() => setViewMode("table")} 
            className={`flex-1 px-2 py-1 text-xs rounded ${viewMode === "table" ? "bg-blue-600 text-white" : "border hover:bg-gray-50"}`}
          >
            Table
          </button>
          <button 
            onClick={() => setViewMode("graph")} 
            className={`flex-1 px-2 py-1 text-xs rounded flex items-center justify-center ${viewMode === "graph" ? "bg-blue-600 text-white" : "border hover:bg-gray-50"}`}
          >
            <BarChart3 className="w-3 h-3 mr-1" /> Graph
          </button>
          <button onClick={exportToCSV} className="px-2 py-1 border rounded hover:bg-gray-50 text-xs">
            <Download className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden sm:flex items-center justify-between mb-6">
        <button onClick={() => navigate('/supplies')} className="inline-flex items-center px-3 py-2 border rounded hover:bg-gray-50 text-sm">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Supplies
        </button>
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold">Supplies Reports</h2>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setViewMode("table")} 
              className={`px-3 py-2 text-sm rounded ${viewMode === "table" ? "bg-blue-600 text-white" : "border hover:bg-gray-50"}`}
            >
              Table
            </button>
            <button 
              onClick={() => setViewMode("graph")} 
              className={`px-3 py-2 text-sm rounded flex items-center ${viewMode === "graph" ? "bg-blue-600 text-white" : "border hover:bg-gray-50"}`}
            >
              <BarChart3 className="w-4 h-4 mr-1" /> Graph
            </button>
            <button onClick={exportToCSV} className="inline-flex items-center px-3 py-2 border rounded hover:bg-gray-50 text-sm">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-3 sm:p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Filters</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2 sm:gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">From</label>
            <input type="date" value={filters.from} onChange={(e)=>setFilters(f=>({...f, from:e.target.value}))} className="w-full border rounded px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">To</label>
            <input type="date" value={filters.to} onChange={(e)=>setFilters(f=>({...f, to:e.target.value}))} className="w-full border rounded px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Vendor</label>
            <select value={filters.vendor} onChange={(e)=>setFilters(f=>({...f, vendor:e.target.value}))} className="w-full border rounded px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm">
              <option value="">All Vendors</option>
              {uniqueVendors.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Product</label>
            <select value={filters.product} onChange={(e)=>setFilters(f=>({...f, product:e.target.value}))} className="w-full border rounded px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm">
              <option value="">All Products</option>
              {uniqueProducts.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Brand</label>
            <select value={filters.brand} onChange={(e)=>setFilters(f=>({...f, brand:e.target.value}))} className="w-full border rounded px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm">
              <option value="">All Brands</option>
              {uniqueBrands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Category</label>
            <select value={filters.category} onChange={(e)=>setFilters(f=>({...f, category:e.target.value}))} className="w-full border rounded px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm">
              <option value="">All Categories</option>
              {uniqueCategories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={fetchOrders} className="flex-1 sm:flex-none px-3 py-2 border rounded text-xs sm:text-sm hover:bg-gray-50">Apply</button>
          <button onClick={()=>{ setFilters({from:"", to:"", vendor:"", product:"", brand:"", category:""}); setTimeout(fetchOrders,0); }} className="flex-1 sm:flex-none px-3 py-2 border rounded text-xs sm:text-sm hover:bg-gray-50">Reset</button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3 mb-4">
        <div className="bg-white rounded p-2 sm:p-3 shadow text-center">
          <div className="text-xs text-gray-500">Subtotal</div>
          <div className="font-semibold text-sm sm:text-base">${subtotal.toFixed(2)}</div>
        </div>
        <div className="bg-white rounded p-2 sm:p-3 shadow text-center">
          <div className="text-xs text-gray-500">Tax</div>
          <div className="font-semibold text-sm sm:text-base">${tax.toFixed(2)}</div>
        </div>
        <div className="bg-white rounded p-2 sm:p-3 shadow text-center">
          <div className="text-xs text-gray-500">Shipping</div>
          <div className="font-semibold text-sm sm:text-base">${shipping.toFixed(2)}</div>
        </div>
        <div className="bg-white rounded p-2 sm:p-3 shadow text-center">
          <div className="text-xs text-gray-500">Other</div>
          <div className="font-semibold text-sm sm:text-base">${other.toFixed(2)}</div>
        </div>
        <div className="bg-white rounded p-2 sm:p-3 shadow text-center col-span-2 sm:col-span-1">
          <div className="text-xs text-gray-500">Total</div>
          <div className="font-bold text-blue-700 text-sm sm:text-base">${total.toFixed(2)}</div>
        </div>
      </div>

      {/* Graph View */}
      {viewMode === "graph" && (
        <div className="bg-white rounded-lg shadow p-3 sm:p-6 mb-4">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 flex items-center">
            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Total Costs Over Time
          </h3>
          {graphData.length > 0 ? (
            <div className="space-y-3 sm:space-y-4">
              {/* Y-axis scale indicator */}
              <div className="flex justify-end text-xs text-gray-500 mb-2">
                Max: ${Math.max(...graphData.map(d => d.total)).toLocaleString()}
              </div>
              <div className="h-48 sm:h-64 flex items-end justify-between gap-1 sm:gap-2 px-1 sm:px-2 relative overflow-x-auto">
                {graphData.map((data, index) => {
                  const maxValue = Math.max(...graphData.map(d => d.total));
                  const containerHeight = window.innerWidth < 640 ? 192 : 256; // h-48 = 192px, h-64 = 256px
                  const topPadding = 20; // Space at top
                  const availableHeight = containerHeight - topPadding;
                  
                  // Calculate height in pixels based on actual data values
                  const heightInPixels = maxValue > 0 ? (data.total / maxValue) * availableHeight : 0;
                  // For zero values, show a small indicator bar, otherwise use calculated height
                  const minHeightPixels = data.total > 0 ? heightInPixels : 4;
                  
                  
                  return (
                    <div key={data.month} className="flex-1 flex flex-col items-center min-w-0">
                      <div 
                        className={`w-full rounded-t transition-colors cursor-pointer relative group border ${
                          data.total > 0 
                            ? 'bg-blue-500 hover:bg-blue-600 border-blue-400' 
                            : 'bg-gray-200 hover:bg-gray-300 border-gray-300'
                        }`}
                        style={{ 
                          height: `${minHeightPixels}px`
                        }}
                        title={`${data.month}: $${data.total.toLocaleString()} (${data.orders} orders)`}
                      >
                        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                          ${data.total.toLocaleString()}
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 mt-2 transform -rotate-45 origin-left whitespace-nowrap min-w-0">
                        {(() => {
                          const [year, month] = data.month.split('-');
                          return new Date(parseInt(year), parseInt(month) - 1, 1).toLocaleDateString('en-US', { month: 'short' });
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="text-center text-sm text-gray-600">
                Total: ${graphData.reduce((sum, d) => sum + d.total, 0).toLocaleString()} across {graphData.reduce((sum, d) => sum + d.orders, 0)} orders
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              No data available for the selected filters
            </div>
          )}
        </div>
      )}

      {/* Mobile cards */}
      <div className={`space-y-3 ${viewMode === "graph" ? "hidden" : "sm:hidden"}`}>
        {filteredOrders.map(o => (
          <div key={o.id} className="border rounded-lg p-3 shadow-sm">
            <div className="flex justify-between">
              <div>
                <div className="text-xs text-gray-500">Date</div>
                <div className="text-sm font-medium">{formatDate(o.order_date)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-500">Total</div>
                <div className="text-sm font-semibold">{Number(o.grand_total).toLocaleString(undefined,{style:'currency',currency:o.currency||'USD',minimumFractionDigits:2,maximumFractionDigits:2})}</div>
              </div>
            </div>
            <div className="mt-1 text-gray-800 text-sm">
              <span className="text-xs text-gray-500">Vendor</span>
              <div className="font-medium">{o.vendor_name}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className={`overflow-x-auto bg-white rounded shadow ${viewMode === "graph" ? "hidden" : "hidden sm:block"}`}>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-600 border-b">
              <th className="py-2 pr-2">Date</th>
              <th className="py-2 pr-2">Vendor</th>
              <th className="py-2 pr-2 text-right">Subtotal</th>
              <th className="py-2 pr-2 text-right">Tax</th>
              <th className="py-2 pr-2 text-right">Shipping</th>
              <th className="py-2 pr-2 text-right">Other</th>
              <th className="py-2 pr-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map(o => (
              <tr key={o.id} className="border-b">
                <td className="py-2 pr-2">{formatDate(o.order_date)}</td>
                <td className="py-2 pr-2">{o.vendor_name}</td>
                <td className="py-2 pr-2 text-right">{Number(o.subtotal).toLocaleString(undefined,{style:'currency',currency:o.currency||'USD',minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                <td className="py-2 pr-2 text-right">{Number(o.tax_total).toLocaleString(undefined,{style:'currency',currency:o.currency||'USD',minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                <td className="py-2 pr-2 text-right">{Number(o.shipping).toLocaleString(undefined,{style:'currency',currency:o.currency||'USD',minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                <td className="py-2 pr-2 text-right">{Number(o.other_total).toLocaleString(undefined,{style:'currency',currency:o.currency||'USD',minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                <td className="py-2 pr-2 text-right font-medium">{Number(o.grand_total).toLocaleString(undefined,{style:'currency',currency:o.currency||'USD',minimumFractionDigits:2,maximumFractionDigits:2})}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


